/**
 * NATLOC API Services
 * 
 * Servicios para interactuar con la API de NATLOC a través del middleware
 */

import { get } from '../client'

/**
 * Realiza una petición al endpoint de autocomplete
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.input - Texto de búsqueda
 * @param {string} params.category - Categoría (electricity, gas)
 * @param {number} params.step - Paso del proceso (en paso 2 será step=3)
 * @returns {Promise<Object>} Resultado de la búsqueda
 */
export const searchAddressAutocomplete = async ({ input, category, step }) => {
  if (!input || input.length < 3) {
    return {
      success: true,
      data: { predictions: [] },
    }
  }

  try {
    const params = new URLSearchParams({
      step: step.toString(),
      category,
      input: input.trim(),
    })

    const url = `autocomplete?${params}`

    // Usar el cliente HTTP de la app
    // El cliente construye: urlBase/entryPoint/url
    // Queremos: /middleware/autocomplete?params
    const data = await get(url, {}, 'middleware')

    // La API devuelve un array directamente con objetos { placeIDGoogle, niceAddress }
    // Mapear a formato esperado por el componente
    const predictions = Array.isArray(data) ? data.map(item => ({
      placeId: item.placeIDGoogle,
      description: item.niceAddress,
      // Mantener datos originales por si se necesitan
      ...item
    })) : []

    return {
      success: true,
      data: { predictions },
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al buscar direcciones',
      data: { predictions: [] },
    }
  }
}

/**
 * Obtiene los detalles horizontales (verticales: piso, puerta, etc.) de una dirección
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.placeId - ID de Google Places (prioridad sobre uuid)
 * @param {string} params.uuid - UUID de la dirección (solo se usa si no hay placeId)
 * @param {string} params.label - Etiqueta de la dirección
 * @param {string} params.category - Categoría (electricity, gas)
 * @param {number} params.step - Paso del proceso
 * @returns {Promise<Object>} Resultado con las verticales
 */
export const getAddressDetails = async ({ placeId, uuid, label, category, step }) => {
  try {
    const params = new URLSearchParams({
      step: step.toString(),
      category,
      label: label.trim(),
    })

    // PRIORIDAD: placeId de Google tiene prioridad sobre uuid
    // Solo usar uuid si NO hay placeId disponible
    const url = placeId 
      ? `detail/horizontal/byplaceid/${placeId}?${params}`
      : uuid
        ? `detail/horizontal/byuuid/${uuid}?${params}`
        : null

    if (!url) {
      throw new Error('Se requiere placeId o uuid para obtener detalles de la dirección')
    }

    const data = await get(url, {}, 'middleware')

    // Verificar si el servicio horizontal falló con código 12
    if (data?.code === 200 && data?.status === 'error' && 
        data?.results?.code === '12' && data?.results?.message === 'Horizontal Not Found') {
      return {
        success: false,
        error: 'Horizontal Not Found',
        errorCode: '12',
        data: { verticals: [] },
      }
    }

    // Verificar si el servicio horizontal falló con código 13 (Not Number)
    if (data?.code === 200 && data?.status === 'error' && 
        data?.results?.code === '13' && data?.results?.message === 'Not Number') {
      return {
        success: false,
        error: 'Not Number',
        errorCode: '13',
        data: { verticals: [] },
      }
    }
    // La API devuelve un array de objetos con { building, staircase, floor, door, restOfAddress, id }
    // Mapear a formato legible incluyendo los campos principales (sin restOfAddress)
    const verticals = Array.isArray(data?.results) ? data.results.map(item => {
      // Construir el label con los campos principales
      const parts = []
      if (item.building) parts.push(`Edif. ${item.building}`)
      if (item.staircase) parts.push(`Esc. ${item.staircase}`)
      if (item.floor) parts.push(`Piso ${item.floor}`)
      if (item.door) parts.push(`Prta. ${item.door}`)

      // Si todos los campos están vacíos, usar "Dirección única"
      const label = parts.length > 0 ? parts.join(', ') : 'Dirección única'

      return {
        label,
        value: item.id,
        // Mantener datos originales
        ...item
      }
    }) : []

    return {
      success: true,
      data: {
        municipalityName: data?.municipalityName,
        number: data?.number,
        postalCode: data?.postalCode,
        provinceName: data?.provinceName,
        roadName: data?.roadName,
        roadTypeName: data?.roadTypeName,
        townName: data?.townName,
        verticals
      },
    }
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al obtener detalles de la dirección',
      data: { verticals: [] },
    }
  }
}

/**
 * Obtiene el detalle completo de una vertical (piso/puerta) y valida el CUPS
 * Este endpoint se llama después de seleccionar una vertical
 * 
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.leadId - ID del lead
 * @param {string} params.verticalId - ID de la vertical seleccionada
 * @param {number} params.step - Paso del proceso
 * @param {string} params.category - Categoría (electricity, gas, gasAndElectricity)
 * @param {string} params.placeId - ID de Google Places
 * @param {string} params.label - Etiqueta de la vertical (ej: "Piso 2, Prta. DER")
 * @param {string} params.energy - Tipo de energía (electricity, gas, gasAndElectricity)
 * @returns {Promise<Object>} Resultado con la dirección completa y CUPS
 */
export const getVerticalDetail = async ({ leadId, verticalId, step, category, placeId, label, energy }) => {
  try {
    // Validar que energy sea válido
    const VALID_ENERGY = ['electricity', 'gas', 'gasAndElectricity']
    if (!VALID_ENERGY.includes(energy)) {
      console.error('[NATLOC API] Invalid energy type:', energy)
      throw new Error(`Invalid energy type: ${energy}. Must be one of: ${VALID_ENERGY.join(', ')}`)
    }

    const params = new URLSearchParams({
      step: step.toString(),
      category,
      id: placeId,
      label: label.trim(),
      energy,
    })

    const url = `lead/${leadId}/detail/vertical/${verticalId}?${params}`

    // TODO: PROVISIONAL - Cabeceras hardcodeadas para testing


    const data = await get(url, {}, 'middleware')

    // Respuesta real de la API:
    // SUCCESS: { code: 200, status: "success", results: { address: { address_id, nice_address, cups_luz, cups_gas, place_id_google } } }
    // ERROR: { code: 200, status: "ERROR", results: { MESSSAGGE: xxx, CODE: xx } }
    // NOT FOUND: { code: 404, status: "not found", results: { message: "not found", code: "02" } }
    // NOT FOUND (alt): { code: 200, status: "NOT FOUND", results: { MESSSAGGE: xxx, CODE: xx } }

    if (data.status === 'success') {
      // La API devuelve los datos dentro de results.address
      // Normalizar los nombres de campos a mayúsculas para mantener compatibilidad
      const addressData = data.results?.address || data.results

      return {
        success: true,
        data: {
          ADDRESS_ID: addressData.address_id,
          NICE_ADDRESS: addressData.nice_address,
          CUPS_LUZ: addressData.cups_luz,
          CUPS_GAS: addressData.cups_gas,
          PLACE_ID_GOOGLE: addressData.place_id_google,
          // Mantener también los campos originales por si se necesitan
          ...addressData,
        },
      }
    } else {
      // Error o Not Found
      // La API puede devolver los campos en mayúsculas o minúsculas
      const errorMessage = data.results?.MESSSAGGE || data.results?.MESSAGE || data.results?.message || 'Error al obtener detalle de la vertical'
      const errorCode = data.results?.CODE || data.results?.code

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: data.status,
        httpCode: data.code, // Incluir el código HTTP (404, 200, etc.)
        data: data.results,
      }
    }
  } catch (error) {
    console.error('[NATLOC API] getVerticalDetail error:', error)

    // Manejar errores HTTP con respuesta del servidor
    if (error.response?.data) {
      const errorCode = error.response.data.results?.CODE || error.response.data.code
      const errorMessage = error.response.data.results?.MESSSAGGE || error.response.data.results?.MESSAGE || error.response.data.message || 'Error al obtener detalle de la vertical'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: error.response.status,
      }
    }

    // Error de red o sin respuesta del servidor
    return {
      success: false,
      error: error.message || 'Error de conexión al obtener detalle de la vertical',
      errorCode: 'NETWORK_ERROR',
      data: null,
    }
  }
}

/**
 * Obtiene información técnica del suministro (consumos, potencias, etc.)
 * @param {Object} params
 * @param {string} params.leadId - ID del lead
 * @param {string} params.addressId - ID de la dirección
 * @param {string} params.energyType - Tipo de energía: 'ELECTRICITY' o 'GAS'
 * @returns {Promise<Object>}
 */
export const getTechnicalInfo = async ({ leadId, addressId, energyType, step, productId }) => {
  try {

    // Extraer solo el UUID si addressId viene como IRI completo
    // Ejemplo: "/middleware/addresses/019xxx..." -> "019xxx..."
    const cleanAddressId = addressId ? addressId.includes('/') ? addressId.split('/').pop() : addressId : null

    const url = leadId ? `sips/technical_infos/${leadId}/${cleanAddressId}/${energyType}?productId=${productId}` : step !== -1 ? `sips/technical_infos/${leadId}/${cleanAddressId}/${energyType}?step=technical_infos` : `sips/technical_infos/${leadId}/${cleanAddressId}/${energyType}?step=calculator`

    const data = await get(url, {}, 'middleware')

    if (data.code === 200 && data.status === 'success') {
      return {
        success: true,
        data: data.results,
      }
    } else {
      // Manejar errores específicos según el diagrama
      const errorCode = data.results?.code || data.code
      const errorMessage = data.results?.MESSAGE || data.message || 'Error al obtener información técnica'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: data.code,
        technicalInfo: errorCode === '08' ? data?.results?.technical_info : null
      }
    }
  } catch (error) {
    console.error('[NATLOC API] getTechnicalInfo error:', error)

    if (error.response?.data) {
      const errorCode = error.response.data.results?.CODE || error.response.data.code
      const errorMessage = error.response.data.results?.MESSAGE || error.response.data.message || 'Error al obtener información técnica'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: error.response.status,
      }
    }

    return {
      success: false,
      error: error.message || 'Error de conexión al obtener información técnica',
      errorCode: 'NETWORK_ERROR',
    }
  }
}

export const getCalculatorData = async ({ leadId, addressId, energyType }) => {
  try {

    const cleanAddressId = addressId.includes('/') ? addressId.split('/').pop() : addressId
    const url = `calculations/${leadId}/${cleanAddressId}/${energyType}`

    const data = await get(url, {}, 'middleware')
    if (data.code === 200 && data.status === 'success') {
      return {
        success: true,
        data: data.results,
      }
    } else {
      // Manejar errores específicos según el diagrama
      const errorCode = data.results?.CODE || data.code
      const errorMessage = data.results?.MESSAGE || data.message || 'Error al obtener información técnica'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: data.code,
      }
    }
  } catch (error) {
    console.error('[NATLOC API] getTechnicalInfo error:', error)

    if (error.response?.data) {
      const errorCode = error.response.data.results?.CODE || error.response.data.code
      const errorMessage = error.response.data.results?.MESSAGE || error.response.data.message || 'Error al obtener información técnica'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: error.response.status,
      }
    }

    return {
      success: false,
      error: error.message || 'Error de conexión al obtener información técnica',
      errorCode: 'NETWORK_ERROR',
    }
  }
}

/**
 * Busca una dirección por CUPS
 * Endpoint: GET /middleware/lead/{leadId}/cups/{cups}?step={step}&category={category}&energy={energy}
 * 
 * La respuesta es idéntica a getVerticalDetail, devuelve ADDRESS_ID, CUPS_LUZ, CUPS_GAS, NICE_ADDRESS
 * 
 * @param {Object} params
 * @param {string} params.cups - CUPS a buscar
 * @param {string} params.leadId - ID del lead
 * @param {string} params.category - Categoría: 'electricity' o 'gas'
 * @param {number} params.step - Paso del proceso
 * @param {string} params.energy - Tipo de energía: 'electricity', 'gas', 'gasAndElectricity'
 * @returns {Promise<Object>}
 */
export const searchByCups = async ({ cups, leadId, category, step, energy }) => {
  try {
    // Validar que energy sea válido
    const VALID_ENERGY = ['electricity', 'gas', 'gasAndElectricity']
    if (!VALID_ENERGY.includes(energy)) {
      console.error('[NATLOC API] Invalid energy type:', energy)
      throw new Error(`Invalid energy type: ${energy}. Must be one of: ${VALID_ENERGY.join(', ')}`)
    }

    // Limpiar CUPS (quitar espacios)
    const cleanCups = cups.replace(/\s/g, '')

    // Construir query params
    const params = new URLSearchParams({
      step: step.toString(),
      category,
      energy,
    })

    // Construir URL: /middleware/lead/{leadId}/cups/{cups}?step=3&category=electricity
    const url = `lead/${leadId}/cups/${cleanCups}?${params}`


    const data = await get(url, {}, 'middleware')
    // La respuesta puede ser:
    // SUCCESS: { code: 200, status: "success", results: { address: { address_id, nice_address, cups_luz, cups_gas, place_id_google } } }
    // ERROR: { code: 200, status: "ERROR", results: { MESSSAGGE: xxx, CODE: xx } }
    // NOT FOUND: { code: 404, status: "not found", results: { message: "not found", code: "02" } }

    if (data.status === 'success') {
      // La API devuelve los datos dentro de results.address (igual que getVerticalDetail)
      const addressData = data.results?.address || data.results

      return {
        success: true,
        data: {
          ADDRESS_ID: addressData.address_id,
          NICE_ADDRESS: addressData.nice_address,
          CUPS_LUZ: addressData.cups_luz,
          CUPS_GAS: addressData.cups_gas,
          PLACE_ID_GOOGLE: addressData.place_id_google,
          // Mantener también los campos originales por si se necesitan
          ...addressData,
        },
      }
    } else {
      // Error o Not Found
      // Manejar tanto mayúsculas como minúsculas en los campos
      const errorMessage = data.results?.MESSSAGGE || data.results?.MESSAGE || data.results?.message || 'CUPS no encontrado'
      const errorCode = data.results?.CODE || data.results?.code

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: data.status,
        httpCode: data.code, // Incluir el código HTTP (404, 200, etc.)
        errorCodeResult: data?.results?.errorCode,
        data: data.results,
      }
    }
  } catch (error) {
    console.error('[NATLOC API] searchByCups error:', error)

    // Manejar errores HTTP con respuesta del servidor
    if (error.response?.data) {
      const errorCode = error.response.data.results?.CODE || error.response.data.code
      const errorMessage = error.response.data.results?.MESSSAGGE || error.response.data.results?.MESSAGE || error.response.data.message || 'CUPS no encontrado'

      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode,
        status: error.response.status,
      }
    }

    // Error de red o sin respuesta del servidor
    return {
      success: false,
      error: error.message || 'Error de conexión al buscar por CUPS',
      errorCode: 'NETWORK_ERROR',
    }
  }
}

/**
 * Obtiene los datos de una dirección por su ID
 * @param {string} addressId - ID de la dirección
 * @returns {Promise<Object>} Datos de la dirección (postalCode, provinceName, municipalityName)
 */
export const getAddressById = async (addressId) => {
  try {
    const url = `addresses/${addressId}`
    const data = await get(url, {}, 'middleware')

    return {
      success: true,
      data: {
        postalCode: data.postalCode,
        provinceName: data.provinceName,
        municipalityName: data.municipalityName,
      },
    }
  } catch (error) {
    console.error('[Address API] getAddressById error:', error)

    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.message || 'Error al obtener datos de la dirección',
        status: error.response.status,
      }
    }

    return {
      success: false,
      error: error.message || 'Error de conexión al obtener datos de la dirección',
      errorCode: 'NETWORK_ERROR',
    }
  }
}

/**
 * Exportaciones por defecto
 */
export default {
  searchAddressAutocomplete,
  getAddressDetails,
  getVerticalDetail,
  getTechnicalInfo,
  searchByCups,
  getAddressById,
}
